/**
 * Staff — invite brokerage_admins / agents, track invitations, view the roster
 * (Workstream B).
 *
 * Reads:  GET  /admin/staff/invitations   GET /admin/staff
 * Writes: POST /admin/staff/invitations    POST /admin/staff/invitations/{id}/revoke
 *
 * Scoping: brokerage_admin acts on their own tenant (leave Tenant ID blank);
 * super_admin must supply a Tenant ID (the API returns 422 otherwise, surfaced
 * inline). The create result carries a one-time accept link to copy/share.
 *
 * Design sweep: the three tables became card lists. A staff row carries more
 * secondary detail than a client row (role, phone, two kinds of licence, a DID,
 * status) and the Manage panel opens a whole editor underneath it, which is
 * what made the table awkward — nine columns, most of them "—", and an
 * expansion row spanning all of them.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminService,
  billingService,
  brokerageSettingsService,
  didRoutesService,
  staffService,
} from "@re-platform/api-client";
import type {
  AgentDID,
  SeatUsage,
  StaffInvitation,
  StaffMember,
  StaffRole,
  StaffSeats,
  TenantMembership,
  TenantWithPlan,
} from "@re-platform/api-client";
import { api } from "../../lib/api";
import {
  Avatar,
  cardList,
  cardShell,
  Collapsible,
  ExpandSign,
} from "../../lib/cards";
import { ContactButtons } from "../../lib/contacts";
import {
  actingClientFor,
  brokerageLabel,
  useMergedQuery,
  type Scoped,
} from "../../lib/mergedQuery";
import { useMemberships } from "../../lib/memberships";
import { useRoles } from "../../lib/roles";
import { AgentLicenses } from "./AgentLicenses";
import { DidRoutes } from "./DidRoutes";
import { DualCapacityEditor, RateCardEditor } from "./RateCard";

type BadgeColor = { bg: string; fg: string };
const MUTED: BadgeColor = {
  bg: "var(--rp-color-surface-2)",
  fg: "var(--rp-color-text-muted)",
};
const STATUS_COLORS: Record<string, BadgeColor> = {
  PENDING: {
    bg: "var(--rp-color-warning-faint)",
    fg: "var(--rp-color-warning)",
  },
  ACCEPTED: {
    bg: "var(--rp-color-success-faint)",
    fg: "var(--rp-color-success)",
  },
  ACTIVE: {
    bg: "var(--rp-color-success-faint)",
    fg: "var(--rp-color-success)",
  },
  SUSPENDED: {
    bg: "var(--rp-color-danger-faint)",
    fg: "var(--rp-color-danger)",
  },
  REVOKED: MUTED,
  EXPIRED: MUTED,
};

function Badge({ status }: { status: string }) {
  const c = STATUS_COLORS[status] ?? MUTED;
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

/** "agent" → "Agent", "brokerage_admin" → "Brokerage admin". */
function roleLabel(role: string): string {
  const words = role.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const inputStyle: React.CSSProperties = {
  padding: "6px 8px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  fontSize: 13,
  width: 220,
};

const fieldLabel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 13,
};

const sectionHeading: React.CSSProperties = {
  fontSize: 16,
  marginTop: 28,
  marginBottom: 10,
};

const mutedNote: React.CSSProperties = {
  color: "var(--rp-color-text-muted)",
  fontSize: 13,
};

/**
 * Secondary line under a name — "Agent · TREC 689214 · DID +1 256 946 6324".
 *
 * Wraps rather than truncating: it shares a wrapping row with the contact
 * buttons, so a narrow card should reflow onto more lines instead of hiding
 * the licence number behind an ellipsis.
 */
function MetaLine({ parts }: { parts: (string | null | undefined)[] }) {
  const shown = parts.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <span style={{ ...mutedNote, fontSize: 12.5 }}>{shown.join(" · ")}</span>
  );
}

function InviteForm({
  tenantId,
  onDone,
}: {
  tenantId: string;
  onDone: (link: string) => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole>("agent");

  // A dual brokerage_admin must say WHICH brokerage they are inviting into —
  // the invite creates a real user with a real role in exactly one tenant, and
  // guessing would put a new agent in the wrong business.
  const memberships = useMemberships();
  const tenants = memberships.data ?? [];
  const isDual = tenants.length > 1;
  const [pickedTenantId, setPickedTenantId] = useState("");
  const targetTenantId = isDual
    ? pickedTenantId || tenants[0]?.tenant_id || ""
    : tenantId.trim();

  const mutation = useMutation({
    mutationFn: () =>
      staffService.createInvitation(actingClientFor(targetTenantId, isDual), {
        email: email.trim(),
        primary_role: role,
        tenant_id: targetTenantId || undefined,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["staff-invitations"] });
      void queryClient.invalidateQueries({ queryKey: ["staff-seats"] });
      setEmail("");
      onDone(created.accept_url);
    },
  });

  const canSubmit = email.trim().includes("@") && !mutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      style={{
        ...cardShell,
        alignItems: "flex-end",
        gap: 16,
        marginBottom: 20,
      }}
    >
      <label style={fieldLabel}>
        <span style={{ color: "var(--rp-color-text-muted)" }}>Email</span>
        <input
          type="email"
          value={email}
          placeholder="new-agent@brokerage.com"
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
      </label>
      <label style={fieldLabel}>
        <span style={{ color: "var(--rp-color-text-muted)" }}>Role</span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as StaffRole)}
          style={{ ...inputStyle, width: 180 }}
        >
          <option value="agent">Agent</option>
          <option value="brokerage_admin">Brokerage admin</option>
        </select>
      </label>
      {isDual && (
        <label style={fieldLabel}>
          <span style={{ color: "var(--rp-color-text-muted)" }}>Brokerage</span>
          <select
            value={targetTenantId}
            onChange={(e) => setPickedTenantId(e.target.value)}
            style={{ ...inputStyle, width: 200 }}
          >
            {tenants.map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>
                {brokerageLabel(t)} · {t.display_name}
              </option>
            ))}
          </select>
        </label>
      )}
      <button
        type="submit"
        className="rp-btn rp-btn--primary rp-btn--sm"
        disabled={!canSubmit}
      >
        {mutation.isPending ? "Inviting…" : "Send invitation"}
      </button>
      {mutation.isError && (
        <span style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
          {mutation.error instanceof Error
            ? mutation.error.message
            : "Invite failed"}
        </span>
      )}
    </form>
  );
}

function SeatChip({ label, seat }: { label: string; seat: SeatUsage }) {
  const full = seat.limit !== null && seat.used >= seat.limit;
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: 999,
        background: full
          ? "var(--rp-color-warning-faint)"
          : "var(--rp-color-surface-2)",
        color: full ? "var(--rp-color-warning)" : "var(--rp-color-text-muted)",
      }}
    >
      {label}: {seat.used}/{seat.limit ?? "∞"}
    </span>
  );
}

function SeatsBar({ seats }: { seats: StaffSeats }) {
  const anyFull =
    (seats.agents.limit !== null && seats.agents.used >= seats.agents.limit) ||
    (seats.admins.limit !== null && seats.admins.used >= seats.admins.limit);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 16,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 12, color: "var(--rp-color-text-muted)" }}>
        Seats
      </span>
      <SeatChip label="Agents" seat={seats.agents} />
      <SeatChip label="Admins" seat={seats.admins} />
      {anyFull && (
        <span style={{ fontSize: 12, color: "var(--rp-color-warning)" }}>
          Seat limit reached — upgrade the plan to invite more.
        </span>
      )}
    </div>
  );
}

/**
 * Re-send a pending staff invite ("they say it never arrived").
 *
 * The server rotates the token, so the fresh accept link is surfaced right here
 * — the admin often needs to hand it over directly when email is the thing that
 * is broken, and any older link they may have copied is now dead.
 */
function ResendButton({
  invitation,
  tenantId,
  isDual,
  onLink,
}: {
  invitation: StaffInvitation;
  // The list may be merged across brokerages, so the action must name the
  // brokerage its row came from.
  tenantId: string;
  isDual: boolean;
  onLink: (url: string) => void;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      staffService.resendInvitation(
        actingClientFor(tenantId, isDual),
        invitation.id,
      ),
    onSuccess: (created) => {
      onLink(created.accept_url);
      void queryClient.invalidateQueries({ queryKey: ["staff-invitations"] });
    },
  });
  if (invitation.status !== "PENDING") return null;
  return (
    <button
      type="button"
      className="rp-btn rp-btn--secondary rp-btn--sm"
      disabled={mutation.isPending}
      title="Send the invitation email again with a new link"
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? "…" : "Resend"}
    </button>
  );
}

function RevokeButton({
  invitation,
  tenantId,
  isDual,
}: {
  invitation: StaffInvitation;
  tenantId: string;
  isDual: boolean;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      staffService.revokeInvitation(
        actingClientFor(tenantId, isDual),
        invitation.id,
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["staff-invitations"] }),
  });
  if (invitation.status !== "PENDING") return null;
  return (
    <button
      type="button"
      className="rp-btn rp-btn--secondary rp-btn--sm"
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? "…" : "Revoke"}
    </button>
  );
}

function SuspendButton({ member }: { member: StaffMember }) {
  const queryClient = useQueryClient();
  const suspended = member.status === "SUSPENDED";
  const mutation = useMutation({
    mutationFn: () =>
      staffService.updateStatus(
        api,
        member.id,
        suspended ? "ACTIVE" : "SUSPENDED",
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["staff-members"] }),
  });
  return (
    // Secondary, not danger — matching the Tenants roster's Suspend/Activate.
    // A red button reads as destructive, and this one is a reversible toggle
    // whose own label offers the way back.
    <button
      type="button"
      className="rp-btn rp-btn--secondary rp-btn--sm"
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? "…" : suspended ? "Reactivate" : "Suspend"}
    </button>
  );
}

/** Loading / error / empty, shared by the three card lists. */
function ListState({
  isLoading,
  isError,
  error,
  isEmpty,
  empty,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isEmpty: boolean;
  empty: string;
}): React.ReactNode {
  if (isLoading) return <p style={mutedNote}>Loading…</p>;
  if (isError) {
    return (
      <p style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
        {error instanceof Error ? error.message : String(error)}
      </p>
    );
  }
  if (isEmpty) return <p style={mutedNote}>{empty}</p>;
  return null;
}

export function Staff() {
  const roles = useRoles();
  const [tenantId, setTenantId] = useState("");
  const [acceptLink, setAcceptLink] = useState<string | null>(null);
  // A SET, not a single id: opening one card used to close whichever other was
  // open, so comparing two agents' licences meant losing your place. Keyed by
  // "tenant:user" because a dual-licensed person appears once per brokerage in
  // a merged roster and those two rows expand independently.
  const [managingKeys, setManagingKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggleManaging = (key: string) =>
    setManagingKeys((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const tenants = useQuery<TenantWithPlan[]>({
    queryKey: ["tenants"],
    queryFn: () => adminService.listTenants(api),
    staleTime: 30_000,
    // super_admin-only endpoint; a brokerage_admin gets their brokerage NAME below.
    enabled: roles.isSuperAdmin,
  });

  // A brokerage_admin has exactly one tenant — a one-option dropdown is noise.
  // Show the brokerage's name instead (their own settings row; no tenant_id).
  const ownBrokerage = useQuery({
    queryKey: ["brokerage-settings"],
    queryFn: () => brokerageSettingsService.get(api),
    staleTime: 60_000,
    enabled: !roles.isSuperAdmin,
    retry: false,
  });

  // A super_admin has no single "own" tenant, so the staff/invitation reads 422
  // with a blank Tenant ID. Default the picker to a real brokerage once the list
  // loads so the page shows data, not an error. brokerage_admins leave it blank —
  // their own tenant is implied server-side.
  //
  // Skip the PLATFORM tenant when anything else exists. It is the operator's own
  // record and carries no agents, brokerage admins or clients, so defaulting to
  // it (which is what happens when you just take the first row — it is the
  // oldest) shows an empty roster and reads as "the console is broken".
  useEffect(() => {
    if (!roles.isSuperAdmin || tenantId) return;
    const all = tenants.data ?? [];
    if (all.length === 0) return;
    const preferred = all.find((t) => t.slug !== "platform") ?? all[0]!;
    setTenantId(preferred.id);
  }, [roles.isSuperAdmin, tenantId, tenants.data]);

  const tid = tenantId.trim() || undefined;

  // Merged only for a DUAL brokerage_admin — someone who administers two
  // brokerages. A super_admin is deliberately excluded: they already have an
  // explicit tenant picker above, and "merge my brokerages" for them would mean
  // fanning out across every tenant on the platform, which is not the same
  // question. Single-tenant admins are unchanged.
  const memberships = useMemberships();
  // A super_admin has no memberships, and useMergedQuery runs ONE query PER
  // membership — so an empty list meant zero queries and an always-empty
  // roster, whichever tenant they picked. Stand in a single synthetic
  // membership for the tenant they selected: same machinery, one brokerage.
  //
  // `actingClientFor` sends X-Acting-Tenant-Id from it, which a super_admin has
  // no membership to satisfy — but the staff routes already take ?tenant_id=
  // (passed as `tid` below) and admin-svc resolves a super_admin's target from
  // that, so the read is scoped correctly either way.
  const selectedTenant = tenants.data?.find((t) => t.id === tid);
  const mergeTenants: TenantMembership[] = roles.isSuperAdmin
    ? selectedTenant
      ? [
          {
            tenant_id: selectedTenant.id,
            slug: selectedTenant.slug,
            display_name: selectedTenant.display_name,
            brokerage_type: selectedTenant.brokerage_type,
            role: "super_admin",
            auth0_org_id: selectedTenant.auth0_org_id ?? null,
          },
        ]
      : []
    : (memberships.data ?? []);

  const invitations = useMergedQuery<StaffInvitation>(
    mergeTenants,
    ["staff-invitations", tid ?? null],
    (client) => staffService.listInvitations(client, { tenant_id: tid }),
    { enabled: mergeTenants.length > 0, actingTenant: !roles.isSuperAdmin },
  );
  const staff = useMergedQuery<StaffMember>(
    mergeTenants,
    ["staff-members", tid ?? null],
    (client) => staffService.listStaff(client, { tenant_id: tid }),
    { enabled: mergeTenants.length > 0, actingTenant: !roles.isSuperAdmin },
  );
  const seats = useQuery<StaffSeats>({
    queryKey: ["staff-seats", tid ?? null],
    queryFn: () => billingService.getStaffSeats(api, { tenant_id: tid }),
    staleTime: 15_000,
    retry: false,
    // super_admin needs a tenant selected; brokerage_admin's is implied.
    enabled: !roles.isSuperAdmin || !!tid,
  });
  // Fetched across the SAME brokerages as the merged roster above — otherwise
  // the second brokerage's agents would show an empty DID, which reads as "no
  // number assigned" rather than "not loaded".
  const dids = useMergedQuery<AgentDID>(
    mergeTenants,
    ["did-routes", tid ?? null],
    (client) => didRoutesService.list(client, tid),
    { enabled: mergeTenants.length > 0, actingTenant: !roles.isSuperAdmin },
  );
  const didByAgentId = new Map(dids.rows.map(({ row: d }) => [d.agent_id, d]));

  // Which kind of brokerage this row belongs to. A dual admin's merged roster
  // mixes both, so the ROW's own membership wins; a super_admin has no
  // membership and falls back to the tenant they picked, a brokerage_admin to
  // their own settings.
  const brokerageTypeOf = (membership?: { brokerage_type?: string }) =>
    membership?.brokerage_type ??
    (roles.isSuperAdmin
      ? tenants.data?.find((t) => t.id === tid)?.brokerage_type
      : ownBrokerage.data?.brokerage_type);

  const allStaff = staff.rows;
  const brokerageAdmins = allStaff.filter(
    (m) => m.row.primary_role === "brokerage_admin",
  );
  const otherStaff = allStaff.filter(
    (m) => m.row.primary_role !== "brokerage_admin",
  );
  // Accepted invitations become roster rows — hide them here to avoid a
  // confusing duplicate; the invitee already shows in the roster below.
  const openInvitations = invitations.rows.filter(
    ({ row: inv }) => inv.status !== "ACCEPTED",
  );

  const rosterState = {
    isLoading: staff.isPending,
    isError: staff.isError,
    error: staff.error,
  };

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Staff</h1>

      {roles.isSuperAdmin ? (
        <label style={{ ...fieldLabel, marginBottom: 16, maxWidth: 360 }}>
          <span style={{ color: "var(--rp-color-text-muted)" }}>Tenant</span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            style={{ ...inputStyle, width: "100%" }}
          >
            <option value="">
              {tenants.isLoading ? "Loading tenants…" : "— Select a tenant —"}
            </option>
            {(tenants.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name} ({t.slug}) · {t.status}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <div style={{ fontSize: 13, marginBottom: 16 }}>
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            Brokerage:{" "}
          </span>
          <strong>{ownBrokerage.data?.legal_name ?? "—"}</strong>
        </div>
      )}

      {seats.data && <SeatsBar seats={seats.data} />}

      <InviteForm tenantId={tenantId} onDone={setAcceptLink} />

      {acceptLink && (
        <div
          style={{
            background: "var(--rp-color-success-faint)",
            border: "1px solid var(--rp-color-success)",
            borderRadius: "var(--rp-radius-lg)",
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span>Invitation sent. One-time accept link:</span>
          <code style={{ fontSize: 12, wordBreak: "break-all" }}>
            {acceptLink}
          </code>
          <button
            type="button"
            className="rp-btn rp-btn--secondary rp-btn--sm"
            onClick={() => void navigator.clipboard?.writeText(acceptLink)}
          >
            Copy link
          </button>
        </div>
      )}

      <h2 style={{ ...sectionHeading, marginTop: 0 }}>Invitations</h2>
      <ListState
        isLoading={invitations.isPending}
        isError={invitations.isError}
        error={invitations.error}
        isEmpty={openInvitations.length === 0}
        empty="No pending invitations."
      />
      <div style={cardList}>
        {openInvitations.map(({ row: inv, tenant }) => (
          <div
            key={`${tenant.tenant_id}:${inv.id}`}
            className="rp-clientcard"
            style={cardShell}
          >
            <Avatar name={inv.email} size={34} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {inv.email}
              </div>
              <div style={{ marginTop: 4 }}>
                <MetaLine
                  parts={[
                    roleLabel(inv.primary_role),
                    invitations.isDual ? brokerageLabel(tenant) : null,
                    `Expires ${inv.expires_at.slice(0, 10)}`,
                  ]}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginLeft: "auto",
              }}
            >
              <Badge status={inv.status} />
              <ResendButton
                invitation={inv}
                tenantId={tenant.tenant_id}
                isDual={invitations.isDual}
                onLink={setAcceptLink}
              />
              <RevokeButton
                invitation={inv}
                tenantId={tenant.tenant_id}
                isDual={invitations.isDual}
              />
            </div>
          </div>
        ))}
      </div>

      <h2 style={sectionHeading}>Brokerage admins</h2>
      <ListState
        {...rosterState}
        isEmpty={brokerageAdmins.length === 0}
        empty="No brokerage admins yet."
      />
      <div style={cardList}>
        {brokerageAdmins.map((s) => (
          <StaffCard
            key={`${s.tenant.tenant_id}:${s.row.id}`}
            {...s}
            isDual={staff.isDual}
            isSuperAdmin={roles.isSuperAdmin}
            open={managingKeys.has(`${s.tenant.tenant_id}:${s.row.id}`)}
            onToggle={() => toggleManaging(`${s.tenant.tenant_id}:${s.row.id}`)}
            did={s.row.agent_id ? didByAgentId.get(s.row.agent_id) : undefined}
            brokerageType={brokerageTypeOf(s.tenant)}
            tid={tid}
          />
        ))}
      </div>

      <h2 style={sectionHeading}>Staff roster</h2>
      <ListState
        {...rosterState}
        isEmpty={otherStaff.length === 0}
        empty="No staff yet."
      />
      <div style={cardList}>
        {otherStaff.map((s) => (
          <StaffCard
            key={`${s.tenant.tenant_id}:${s.row.id}`}
            {...s}
            isDual={staff.isDual}
            isSuperAdmin={roles.isSuperAdmin}
            open={managingKeys.has(`${s.tenant.tenant_id}:${s.row.id}`)}
            onToggle={() => toggleManaging(`${s.tenant.tenant_id}:${s.row.id}`)}
            did={s.row.agent_id ? didByAgentId.get(s.row.agent_id) : undefined}
            brokerageType={brokerageTypeOf(s.tenant)}
            tid={tid}
          />
        ))}
      </div>
    </>
  );
}

/**
 * A titled, independently collapsible sub-section of a card's Manage panel.
 *
 * Thin wrapper over the shared Collapsible: this one supplies the panel's own
 * box, which the roster's Manage area draws around each sub-section.
 */
function ManagePanel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: "var(--rp-color-surface)",
        border: "1px solid var(--rp-color-border)",
        borderRadius: "var(--rp-radius-md)",
        padding: 14,
      }}
    >
      <Collapsible title={title} headingLevel="h3">
        {children}
      </Collapsible>
    </div>
  );
}

function StaffCard({
  row: m,
  tenant,
  isDual,
  isSuperAdmin,
  open,
  onToggle,
  did,
  brokerageType,
  tid,
}: Scoped<StaffMember> & {
  isDual: boolean;
  isSuperAdmin: boolean;
  open: boolean;
  onToggle: () => void;
  did: AgentDID | undefined;
  brokerageType: string | undefined;
  tid: string | undefined;
}) {
  // Manage (phone + licenses) is offered for agents and brokerage admins.
  // Agents: any admin may manage them. A brokerage admin row may only be
  // managed by a super_admin — a broker sees the button disabled.
  const manageable =
    m.primary_role === "agent" || m.primary_role === "brokerage_admin";
  const canManage = m.primary_role === "agent" || isSuperAdmin;
  const name = m.full_name ?? m.email;

  const licences =
    [
      m.nmls_id && `NMLS ${m.nmls_id}`,
      m.trec_license_number && `TREC ${m.trec_license_number}`,
    ]
      .filter(Boolean)
      .join(" · ") ||
    (m.license_count > 0 ? `${m.license_count} on file` : null);

  return (
    <div>
      <div
        className="rp-clientcard"
        style={{
          ...cardShell,
          // Square off the bottom so the Manage panel reads as part of the
          // same card rather than a second one floating underneath.
          //
          // One shorthand rather than overriding the two bottom corners:
          // cardShell already sets `borderRadius`, and adding longhands on top
          // means React has to REMOVE them on collapse while the shorthand
          // stays — which it warns about, because the result depends on
          // property order.
          borderRadius: open
            ? "var(--rp-radius-lg) var(--rp-radius-lg) 0 0"
            : "var(--rp-radius-lg)",
        }}
      >
        <Avatar name={name} />
        {/* minWidth caps how far the name column may shrink; below it the
            action cluster wraps to its own line instead of the meta and the
            contact buttons being squeezed to nothing. */}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "var(--rp-color-text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {m.full_name ?? "—"}
          </div>
          {/* The meta and the contact buttons share one WRAPPING row, the
              same shape Clients uses — that is what lets email/phone drop to
              a new line as the window narrows rather than just shrinking. */}
          <div
            style={{
              marginTop: 4,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <MetaLine
              parts={[
                roleLabel(m.primary_role),
                isDual ? brokerageLabel(tenant) : null,
                licences,
                did?.did ? `DID ${did.did}` : null,
              ]}
            />
            <ContactButtons email={m.email} phone={m.phone_e164} />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginLeft: "auto",
          }}
        >
          {/* Status, then the status ACTION, then Manage last — the order the
              Tenants roster uses. Manage is the one control on every card that
              does the same thing everywhere, so it sits in the same place. */}
          <Badge status={m.status} />
          {manageable && canManage && <SuspendButton member={m} />}
          {manageable && (
            <button
              type="button"
              className="rp-btn rp-btn--secondary rp-btn--sm"
              disabled={!canManage}
              aria-expanded={open}
              title={
                canManage
                  ? undefined
                  : "Only a super admin can manage a brokerage admin"
              }
              onClick={() => canManage && onToggle()}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <ExpandSign open={open} />
              Manage
            </button>
          )}
        </div>
      </div>

      {open && (
        <div
          style={{
            border: "1px solid var(--rp-color-border)",
            borderTop: "none",
            borderRadius: "0 0 var(--rp-radius-lg) var(--rp-radius-lg)",
            background: "var(--rp-color-surface-2)",
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <ManagePanel title="Agent licence details">
            <AgentLicenses member={m} />
          </ManagePanel>
          {/* TXR-1501 is a TREC form. A mortgage brokerage originates no
              real estate agreements, so there is no rate card to set —
              the agent's own /rate-card page already says exactly that. */}
          {m.primary_role === "agent" && brokerageType !== "MORTGAGE" && (
            <ManagePanel title="Rate card">
              <RateCardEditor userId={m.id} />
            </ManagePanel>
          )}
          {/* Unlike the rate card, this applies to BOTH brokerage types: the
              agent it concerns is precisely the one acting on both sides. */}
          {m.primary_role === "agent" && (
            <ManagePanel title="Dual capacity disclosure">
              <DualCapacityEditor userId={m.id} />
            </ManagePanel>
          )}
          {m.primary_role === "agent" && (
            <ManagePanel title="Twilio number (DID)">
              <DidRoutes member={m} tenantId={tid} />
            </ManagePanel>
          )}
        </div>
      )}
    </div>
  );
}
